/**
 * ListRow Component
 *
 * Base component for list items with avatar/content/actions layout.
 * Supports clickable (button), link, and static variants.
 * Now supports secondary content with responsive sandwich layout.
 *
 * @module components/common/ListRow
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import type { ListRowProps } from './ListRow.types';
import styles from './ListRow.module.css';

/**
 * ListRow Component
 *
 * @example
 * ```tsx
 * // Basic usage with content only
 * <ListRow content={<Text>Item content</Text>} />
 *
 * // With avatar and actions
 * <ListRow
 *   avatar={<Avatar src={user.avatar} />}
 *   content={<Text>{user.name}</Text>}
 *   actions={<ActionIcon><IconEdit /></ActionIcon>}
 * />
 *
 * // Clickable row with onClick
 * <ListRow
 *   content={<Text>Click me</Text>}
 *   onClick={handleClick}
 * />
 *
 * // As a link with active state
 * <ListRow
 *   content={<Text>Navigation item</Text>}
 *   href="/dashboard"
 *   isActive={pathname === '/dashboard'}
 * />
 *
 * // Disabled state
 * <ListRow
 *   content={<Text>Disabled item</Text>}
 *   isDisabled
 * />
 *
 * // With secondary content (responsive sandwich layout)
 * <ListRow
 *   avatar={<Avatar src={workspace.avatar} />}
 *   content={<Text>{workspace.name}</Text>}
 *   secondaryContent={<Badge>5 members</Badge>}
 *   actions={<ActionIcon><IconSettings /></ActionIcon>}
 * />
 * ```
 */
export const ListRow = memo(function ListRow({
  avatar,
  content,
  secondaryContent,
  actions,
  onClick,
  href,
  isActive = false,
  isDisabled = false,
  className,
}: ListRowProps) {
  // Determine if row is interactive
  const isClickable = Boolean(onClick || href) && !isDisabled;

  // Build class list
  const rowClasses = [
    styles.row,
    isClickable && styles.clickable,
    isActive && styles.active,
    isDisabled && styles.disabled,
    secondaryContent && styles.hasSecondary,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Row inner content
  const rowContent = (
    <>
      {avatar && <div className={styles.avatar}>{avatar}</div>}
      <div className={styles.contentWrapper}>
        <div className={styles.content}>{content}</div>
        {secondaryContent && (
          <div className={styles.secondaryContent}>{secondaryContent}</div>
        )}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </>
  );

  // Render as link
  if (href && !isDisabled) {
    return (
      <Link to={href} className={`${styles.link} ${rowClasses}`}>
        {rowContent}
      </Link>
    );
  }

  // Render as button (clickable with onClick)
  if (onClick && !isDisabled) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${styles.button} ${rowClasses}`}
        disabled={isDisabled}
      >
        {rowContent}
      </button>
    );
  }

  // Render as static div
  return <div className={rowClasses}>{rowContent}</div>;
});

ListRow.displayName = 'ListRow';
